import { Injectable, computed, effect, inject, signal } from '@angular/core';

import { ScreenNavigationService } from './screen-navigation';

export enum SoundEffect {
  PageTransition = 'page_transition',
  Yes = 'yes',
  No = 'no',
  Invalid = 'invalid',
  Win = 'win',
  Loss = 'loss',
  Error = 'error',
}

const SOUNDS_PATH = 'assets/sounds';
const GAME_SCREEN = 'game';
const UNLOCK_EVENTS = ['pointerdown', 'keydown', 'touchstart'] as const;
const SOUND_ENABLED_STORAGE_KEY = 'trivia:sound-enabled';

const BACKGROUND_MUSIC_VOLUME = 0.4;
const GAME_MUSIC_VOLUME = 0.1;
const EFFECT_VOLUME = 0.6;

const EFFECT_FILES: Record<SoundEffect, string> = {
  [SoundEffect.PageTransition]: 'page_transition.mp3',
  [SoundEffect.Yes]: 'yes.mp3',
  [SoundEffect.No]: 'no.mp3',
  [SoundEffect.Invalid]: 'invalid.mp3',
  [SoundEffect.Win]: 'win.mp3',
  [SoundEffect.Loss]: 'loss.mp3',
  [SoundEffect.Error]: 'error.mp3',
};

// no.mp3 pedido ~20% mais alto que os demais efeitos (0.6 -> 0.72).
const EFFECT_VOLUME_OVERRIDES: Partial<Record<SoundEffect, number>> = {
  [SoundEffect.No]: 0.5,
};

/**
 * Toca as músicas de fundo (uma por tela — `background.mp3` fora do jogo,
 * `game_music.mp3` na game-screen) e os efeitos sonoros pontuais do jogo.
 * A troca de trilha de fundo segue `ScreenNavigationService.screen()` automaticamente;
 * efeitos pontuais (respostas, transição de tela, resultado) são disparados sob demanda
 * pelos componentes via `playEffect`.
 *
 * Preferência de som (ligado/desligado) é escolhida pelo usuário (botão na top-app-bar)
 * e persistida em localStorage — na ausência de valor salvo, o padrão é ligado.
 */
@Injectable({ providedIn: 'root' })
export class AudioService {
  private readonly screenNavigation = inject(ScreenNavigationService);

  // Navegadores só permitem autoplay com áudio ligado após um gesto do usuário.
  // As trilhas começam mudas e tocando (autoplay mudo é sempre permitido) para já
  // estarem em andamento assim que a 1ª interação liberar o som (ver `isMuted`).
  private readonly browserUnlocked = signal(false);
  private readonly soundEnabled = signal(this.readStoredSoundPreference());
  readonly isSoundEnabled = this.soundEnabled.asReadonly();

  private readonly isMuted = computed(() => !this.browserUnlocked() || !this.soundEnabled());

  private readonly backgroundMusic = this.createTrack('background.mp3', BACKGROUND_MUSIC_VOLUME, true);
  private readonly gameMusic = this.createTrack('game_music.mp3', GAME_MUSIC_VOLUME, true);

  private readonly effects: Record<SoundEffect, HTMLAudioElement> = Object.fromEntries(
    Object.entries(EFFECT_FILES).map(([key, fileName]) => [
      key,
      this.createTrack(fileName, EFFECT_VOLUME_OVERRIDES[key as SoundEffect] ?? EFFECT_VOLUME, false),
    ]),
  ) as Record<SoundEffect, HTMLAudioElement>;

  constructor() {
    this.backgroundMusic.muted = true;
    this.gameMusic.muted = true;

    effect(() => {
      const isGameScreen = this.screenNavigation.screen() === GAME_SCREEN;
      this.switchBackgroundTrack(
        isGameScreen ? this.gameMusic : this.backgroundMusic,
        isGameScreen ? this.backgroundMusic : this.gameMusic,
      );
    });

    effect(() => {
      const muted = this.isMuted();
      this.backgroundMusic.muted = muted;
      this.gameMusic.muted = muted;
    });

    this.registerUnlockListeners();
  }

  toggleSound(): void {
    const next = !this.soundEnabled();
    this.soundEnabled.set(next);

    try {
      localStorage.setItem(SOUND_ENABLED_STORAGE_KEY, String(next));
    } catch {
      // localStorage indisponível (modo privado, etc.) — preferência só dura a sessão atual.
    }
  }

  playBackgroundMusic(): void {
    const isGameScreen = this.screenNavigation.screen() === GAME_SCREEN;
    const track = isGameScreen ? this.gameMusic : this.backgroundMusic;
    void track.play().catch(() => {});
  }

  playEffect(sound: SoundEffect): void {
    if (!this.soundEnabled()) {
      return;
    }

    const audio = this.effects[sound];
    audio.currentTime = 0;
    void audio.play().catch(() => {});
  }

  playPageTransition(): void {
    this.playEffect(SoundEffect.PageTransition);
  }

  private switchBackgroundTrack(trackToPlay: HTMLAudioElement, trackToStop: HTMLAudioElement): void {
    trackToStop.pause();
    trackToStop.currentTime = 0;
    void trackToPlay.play().catch(() => {});
  }

  private registerUnlockListeners(): void {
    const unlock = () => {
      if (this.browserUnlocked()) {
        return;
      }
      this.browserUnlocked.set(true);

      // Reforça o play() da trilha ativa: se a tentativa muda no boot tiver falhado
      // por qualquer motivo, o áudio ficaria mudo até a próxima troca de tela.
      const isGameScreen = this.screenNavigation.screen() === GAME_SCREEN;
      this.switchBackgroundTrack(
        isGameScreen ? this.gameMusic : this.backgroundMusic,
        isGameScreen ? this.backgroundMusic : this.gameMusic,
      );

      for (const eventName of UNLOCK_EVENTS) {
        document.removeEventListener(eventName, unlock);
      }
    };

    for (const eventName of UNLOCK_EVENTS) {
      document.addEventListener(eventName, unlock, { passive: true });
    }
  }

  private readStoredSoundPreference(): boolean {
    try {
      const stored = localStorage.getItem(SOUND_ENABLED_STORAGE_KEY);
      return stored === null ? true : stored === 'true';
    } catch {
      return true;
    }
  }

  private createTrack(fileName: string, volume: number, loop: boolean): HTMLAudioElement {
    const audio = new Audio(`${SOUNDS_PATH}/${fileName}`);
    audio.volume = volume;
    audio.loop = loop;
    return audio;
  }
}
